/** The URL contract: every search parameter the app reads, validated in one place.
 *
 * **It is its own module because a whitelist that drops a parameter is worse than no whitelist.**
 * `validateSearch` strips anything it does not return, so a control that writes a parameter the
 * validator forgot changes the address bar and nothing else — on every screen, with neither side
 * failing. That happened once with `from`/`to` (the period selector wrote them, `useScreen` read
 * them back as undefined), and it is the reason the register's filters are listed as data here
 * rather than named one by one in a return statement: `search.test.ts` round-trips **every key in
 * `FILTER_KEYS`**, so adding a filter without adding it to the URL fails a test instead of
 * silently doing nothing.
 *
 * Extracted from `router.tsx` so this can be tested without constructing a router — the whole
 * route tree, and therefore every screen, would otherwise be imported to assert a string.
 *
 * **One correction to the paragraph above, measured rather than assumed** (EXP-39, 2026-10-09).
 * *"`validateSearch` strips anything it does not return"* is not true of what the screens actually
 * read. Every screen reads with `useSearch({ strict: false })`, and that returns the location's own
 * parsed search rather than this function's output: removing `from` and `to` from the return
 * statement fails `search.test.ts` and **no screen test at all**, which was checked by doing it.
 *
 * So this is the **contract and the typed accessor**, not the enforcement point it was described
 * as. That does not make it optional — it is what gives every screen a typed, validated value
 * instead of `unknown`, it is the one list a reader can check a control against, and a key missing
 * from it disappears the day a screen reads strictly or the router changes behaviour. But a
 * parameter added to a control and forgotten here fails nothing today, which means the real guard
 * is a **screen** test driving the control and asserting the rows change — `Inventory.test.tsx`
 * does that for `rows`. Saying this plainly rather than leaving the stronger sentence standing:
 * a docstring the next reader reasons from has to be true.
 */

import { isTableKey } from "../components/ui/tablePower/urlKeys";
import { readAdvancedScope2 } from "../lib/displayPreferences";

/** The register's filters, as URL keys.
 *
 * In the URL rather than in `useState` for two reasons that are the same reason: a filtered list is
 * a **view somebody shares** and a view somebody comes back to. EXP-12 asks for return-to-list
 * filters, and the honest way to preserve them across a record page and the browser's Back button
 * is to put them where the browser already keeps state.
 *
 * `q` rather than `query`: it is in a URL people paste.
 */
export const FILTER_KEYS = [
  "scope",
  "entity",
  "site",
  "sites",
  "month",
  "category",
  "source",
  "status",
  "quality",
  "owner",
  "q",
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

/** The most sites the register's `sites` filter will carry: the server's own bound on a repeated
 * `facility_id` (`GET /figures`), so a list the screen accepts is a list the API would. */
export const MAX_SITES = 200;

/** The register's **list of sites** filter (`sites`), as the ids it names.
 *
 * One URL value holding several facility ids joined by commas, because the register's filters are
 * one string each (`Filters`) and a list that needed a second representation would be two kinds of
 * "which sites" in one place. The year-on-year bridge writes it: a total that is a sum of several
 * sites opens the register for exactly those sites, and `site` (one id) could not say that.
 *
 * **Validated here, in the contract.** A token is an identifier (letters, digits, `-`, `_`), at most
 * 64 characters; anything else is dropped rather than carried, repeats are removed and the list is
 * capped at `MAX_SITES`. Every token is still checked against the sites the window holds
 * (`register.effective`), so a pasted id that names nothing narrows nothing, and the banner says so.
 * Arrays and numbers are accepted because the router's parser produces them from a pasted link.
 */
export function siteIds(value: unknown): string[] {
  const parts: unknown[] = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : typeof value === "number"
        ? [value]
        : [];
  const seen = new Set<string>();
  for (const part of parts) {
    const token = (
      typeof part === "number" ? String(part) : typeof part === "string" ? part : ""
    ).trim();
    if (/^[0-9A-Za-z][0-9A-Za-z_-]{0,63}$/.test(token)) seen.add(token);
    if (seen.size >= MAX_SITES) break;
  }
  return [...seen];
}

/** What the register reads from the URL. Every value is a string or absent — never a default
 * baked in here, because "absent" and "all" must be the same thing in one place only. */
export type FilterSearch = { [K in FilterKey]?: string };

/** What every screen reads from the URL.
 *
 * `method` is here rather than in component state because it changes Scope 2 figures on **every**
 * screen, including the Emissions total. A local toggle would let two screens disagree about which
 * inventory is being shown, and an unlabelled Scope 2 figure is not interpretable.
 */
export interface ScreenSearch extends FilterSearch {
  /** Which workspace. Absent means "the first one", resolved once organisations load. */
  org?: string;
  method: "location" | "market";
  /** The reporting window, as dates (F11). **These have to be validated here**: anything this
   * function does not return is stripped on read, so the period selector wrote `from` and `to` into
   * the URL and `useScreen` read them back as undefined — a selector that changed the address bar
   * and nothing else, on every screen. A whitelist that silently drops a parameter is worse than no
   * whitelist, because the writer and the reader disagree without either failing. */
  from?: string;
  to?: string;
  /** A calculation id — the drawer shows that figure's receipt. */
  receipt?: string;
  /** Which part of that receipt to land on (`source`, `verifier`, `factor`, `ledger`...), so a click
   * on one link of an evidence meter opens the node that explains it. Belongs to the open drawer. */
  part?: string;
  /** A breakdown key, e.g. `scope-2` or a facility id — the drawer lists the figures behind it. */
  breakdown?: string;
  /** A record id to ask an assurance question about (gate 27). Screen-owned: `/assurance` seeds its
   * raise form from it, and `keepSearch` deliberately does not carry it - it belongs to the
   * screen being left, as `receipt` does. */
  record?: string;
  /** Which tab of the record page. In the URL so a tab is linkable — an auditor's question is
   * "look at this record's revision history", not "open this record and click the fifth tab". */
  tab?: string;
  /** Which **filing obligation** the disclosure screens are about (UX-01, ADR-0029).
   *
   * It was `useState` on two screens, and that is the defect rather than the style. Three things
   * were impossible while it was:
   *
   * - **Nobody could be sent to a filing.** `/reports` opened on whichever obligation sorted
   *   first, so a link to *this year's SECR return for the parent* did not exist — and UX-01's
   *   acceptance clause is that an executive can **reach** the comparison. A screen whose subject
   *   is chosen by a click nobody can record is a screen that can only be described, not shared.
   * - **Back did not come back.** Open a filing, open a record from it, press Back: component
   *   state is gone and the screen re-opens on the default obligation, silently (EXP-29's "open,
   *   back" pair, EXP-12's return-to-list clause). Nothing failed, which is what made it survive.
   * - **Two screens about the same filing disagreed.** Reports and Prior disclosures each held
   *   their own selection, so moving between them changed the subject without saying so.
   *
   * **It is not an authorization surface, and the distinction matters.** `org` is ignored and
   * `entity` is never read from a URL because *which tenant may I see* is a membership question;
   * an obligation id is a **row** inside the tenant the session already names, resolved under the
   * session's own scope and authorized by the route before anything about it is described
   * (ADR-0029: resolve, then authorize, then describe). The screens narrow that further: a value
   * that is not in the obligation list the server just returned is **not in force** — the
   * register's `?status=banana` rule, applied to the one parameter that names a filing.
   */
  filing?: string;
  /** A census window state (`missing`, `unknown`, ...) the Coverage screen narrows its streams to.
   *
   * It is **not** a `FilterKey` and not the register's `status`: that one is a figure's evidence
   * status with its own closed set, and a Coverage link carrying `status=missing` onto Figures (or
   * the reverse) would be a value that means two things. Screen-owned (`SCREEN_OWNED_KEYS`), so it
   * does not follow the reader to another screen. The `site` it pairs with is the register's own
   * `site` filter - the same fact, a facility id. Coverage validates both against what the census
   * holds and says so when one matches nothing (`sourceNarrowing.ts`). */
  state?: string;
  /** The place whose section Help opens first (a `NavId`, from the question mark in the top bar).
   * Screen-owned: it belongs to Help and follows nobody to another screen. Not an authorization
   * surface and not a filter: Help shows the section when it names a place and ignores it otherwise. */
  place?: string;
  /** Which standard report the Reports library has open (a key from `GET /data-reports`), so a
   * report is a link. Screen-owned: it follows nobody to another screen. */
  report?: string;
  /** How many rows of the register the reader has asked to be drawn (EXP-39's large-register half).
   *
   * **A ceiling the reader raised, never a page number.** The distinction is the whole reason it is
   * safe to put in a URL. A `page` parameter goes stale against a selection that shrinks — filter
   * to three figures while standing on page nine and the register renders **empty**, which is the
   * defect `effective()` exists to prevent and the one state in which a reader concludes their
   * figures are missing. A ceiling cannot do that: a ceiling above the selection shows all of it,
   * and a ceiling below `RENDER_CAP` is floored back up to it.
   *
   * So there is no upper bound to validate against either. It is a bound on `slice` over a list the
   * client already holds, so the data bounds it; `?rows=99999999` lists every figure there is.
   *
   * In the URL rather than in `useState` for EXP-12's reason, which is the filters' reason: a reader
   * who expanded to row 800, opened that record and came back has not been returned anywhere if the
   * register snaps back to 500. It is **not** a `FilterKey` — it selects nothing, changes no total
   * and must never become a chip, because a chip would label a complete register "filtered".
   */
  rows?: number;
  /** Where the reader is in the ledger's chain: the **exclusive** sequence a page ends at.
   *
   * In the URL rather than in `useState` because a page of the chain is a thing an auditor sends
   * somebody — the same reason the receipt drawer is addressed by URL. It also makes the browser's
   * Back button the "newer entries" control, which is why there is no `after`: the API has no
   * cursor in that direction, and one invented on the client would be a second answer to where in
   * the chain a reader is.
   *
   * It is **not** a `FilterKey` and must never become a chip: it selects nothing and hides nothing,
   * so labelling the chain "filtered" because somebody paged back would be a false statement about
   * a record whose completeness is the whole point.
   *
   * A cursor rather than a page number, for `rows`' reason one step on: a `page` goes stale against
   * a list that has grown, and this list grows every time anybody does anything. `seq` is dense,
   * monotonic and never deleted, so a cursor over it cannot skip or repeat a row — and a cursor
   * past the end comes back as a page with nothing on it rather than as a silent empty register.
   */
  before?: number;
  /** A table's own search text (`fq`). Screen-owned: see `isTableKey`. The table's filters are the
   * `f_<filter id>` keys, each a comma-joined list of URL-encoded values; they pass through
   * `validateSearch` untyped, because their ids are the table's own (`useUrlFilters`). */
  fq?: string;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** A count from a URL: a whole number of at least one, or absent.
 *
 * Both shapes, because both arrive. A `navigate` from this app passes a `number`; a pasted or
 * hand-edited link arrives as whatever the router's parser made of the text. `Number("")` is `0`
 * and `Number(" ")` is `0`, so the string is tested before it is converted rather than after —
 * `?rows=` would otherwise read as a register asked to draw nothing.
 *
 * Anything else is **absent**, not clamped to a default here: "absent" and "the standard cap" have
 * to be the same thing in one place only, and that place is `register.ts`.
 */
function asCount(value: unknown): number | undefined {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value.trim())
        ? Number(value.trim())
        : NaN;
  return Number.isSafeInteger(n) && n >= 1 ? n : undefined;
}

export function validateSearch(search: Record<string, unknown>): ScreenSearch {
  // **The basis: the address's if it names one, else the one the person asked to see everywhere.**
  // "Show market-based Scope 2 everywhere" (My settings > Advanced) was stored and read by nothing
  // (G-Wviewer-04). It is read here because this is where an address with no `method` becomes a
  // basis, and the router writes the answer back into the address, so the choice is then explicit
  // and linkable; a link that names one still wins.
  const method =
    search.method === "market" || search.method === "location"
      ? search.method
      : readAdvancedScope2()
        ? "market"
        : "location";
  // A loop rather than eight more lines in the object literal: the list is the contract, and a
  // contract spelled out twice is one that eventually disagrees with itself.
  const filters = {} as Record<FilterKey, string | undefined>;
  for (const key of FILTER_KEYS) {
    // `sites` is a list of ids, so it is validated as one; every other key is a string.
    filters[key] =
      key === "sites" ? siteIds(search[key]).join(",") || undefined : asString(search[key]);
  }
  // A table's filters (`f_<id>`, `fq`): kept as the strings they were written as, so a chip a person
  // shared arrives as the chip. What they select is the table's to validate against its own options.
  const table: Record<string, string> = {};
  for (const [key, raw] of Object.entries(search)) {
    if (!isTableKey(key)) continue;
    const text = asString(
      Array.isArray(raw) ? raw.join(",") : typeof raw === "number" || typeof raw === "boolean" ? String(raw) : raw,
    );
    if (text !== undefined) table[key] = text;
  }
  return {
    ...table,
    ...filters,
    org: asString(search.org),
    method,
    from: asString(search.from),
    to: asString(search.to),
    receipt: asString(search.receipt),
    part: asString(search.part),
    record: asString(search.record),
    breakdown: asString(search.breakdown),
    tab: asString(search.tab),
    filing: asString(search.filing),
    state: asString(search.state),
    place: asString(search.place),
    report: asString(search.report),
    rows: asCount(search.rows),
    before: asCount(search.before),
  };
}

/** Carry the whole URL context across a `Link`, unchanged.
 *
 * The context is the window, the Scope 2 basis **and** the register's filters, and a navigation
 * that keeps some of them is worse than one that keeps none: `<a href={`/records/${id}?method=
 * location`}>` is what the register's row used to be, so opening a record from a market-based view
 * silently moved the reader to the other inventory, and the period went back to the workspace
 * default. One function, so a new parameter is carried everywhere the moment it is validated.
 *
 * `method` is filled rather than passed through because it is the one parameter that is not
 * optional — a Scope 2 figure with no stated basis cannot be interpreted.
 */
export function keepSearch(prev: Partial<ScreenSearch>): ScreenSearch {
  // Assembled rather than spread, so what travels is a decision rather than whatever happened to
  // be in the address bar. The window, the basis and the register's filters belong to the
  // workspace and follow the reader; `receipt`, `breakdown` and `tab` belong to the screen being
  // left — a receipt id carried onto a record page opens a drawer over it, and a tab chosen for
  // one record is not a tab anybody chose for another.
  const next: ScreenSearch = {
    method: prev.method ?? "location",
    from: prev.from,
    to: prev.to,
    // **Carried, for the same reason the filters are** (EXP-12). The register's own "back to the
    // inventory" link is this function, so dropping it would return somebody who had expanded to
    // row 800 to a list of 500 — the filters' defect in a different parameter, and silent in
    // exactly the same way.
    rows: prev.rows,
    // **Carried, because it is context rather than a screen's own state.** Which filing a person
    // is working on is the same class of fact as which year: two screens read it, both mean the
    // same thing by it, and a navigation that dropped it would change the subject on arrival. It
    // is **not** a `FilterKey` and must never become a chip — it chooses a filing, not rows of one.
    filing: prev.filing,
  };
  for (const key of FILTER_KEYS) next[key] = prev[key];
  return next;
}

/** The parameters a screen owns, which must not follow the reader to another screen.
 *
 * **Navigation carried the whole address bar** (US walkthrough C7). The rail's links build their
 * search as `{...prev, method, from, to}`, so a receipt opened on one page was still open, as a
 * drawer, over the next one - People, Bulk edit, the evidence library - and squeezed the cards
 * behind it. `keepSearch` already says these belong to the screen being left; the rail did not use
 * it. These are the keys that say *which receipt is open*, *which breakdown*, *which record a
 * question is about*, *which tab* and *where in the ledger* - all of them a statement about one
 * screen's contents.
 */
export const SCREEN_OWNED_KEYS = [
  "receipt",
  "part",
  "breakdown",
  "record",
  "tab",
  "before",
  "state",
  "place",
  "report",
  // The bridge's "these sites" narrowing belongs to the register it opened, with a banner there
  // that says what it is. Carried to another screen it would narrow a list that shows no such
  // banner: the register's silently dropped filter, in reverse.
  "sites",
] as const;

/** The two screens that are about the same filing, so `filing` survives a move between them.
 *
 * `filing` is context rather than screen state (the paragraph on `ScreenSearch.filing`: two screens
 * about one filing must not disagree). It is **context for those two only**: carried to Approvals
 * and back, it made the Reports link reopen whatever filing was last looked at instead of the list.
 */
export const FILING_SCREENS: readonly string[] = ["/reports", "/prior-disclosures"];

/** What to keep of the current search when the destination is a different screen.
 *
 * Same screen, or no screen yet (first load, a pasted link, the deep link sign-in restores): the
 * search is returned as it stands, so a shared link and an in-page control (open a receipt, change
 * a tab) are untouched. Different screen: the screen-owned keys go, and `filing` goes unless both
 * ends are filing screens. An explicit value set by the link being followed is applied **after**
 * this, so a deliberate `search={{ filing: id }}` or `tab: "register"` still arrives.
 */
export function searchForScreen(
  search: Record<string, unknown>,
  fromRouteId: string | undefined,
  toRouteId: string,
): Record<string, unknown> {
  if (fromRouteId === undefined || fromRouteId === toRouteId) return search;
  const next: Record<string, unknown> = { ...search };
  for (const key of SCREEN_OWNED_KEYS) delete next[key];
  // A table's own filters (`f_<id>`, `fq`) are the screen's too: carried to another screen they would
  // narrow a list that draws no chip for them.
  for (const key of Object.keys(next)) if (isTableKey(key)) delete next[key];
  if (!(FILING_SCREENS.includes(fromRouteId) && FILING_SCREENS.includes(toRouteId))) {
    delete next.filing;
  }
  return next;
}
